import MyLocationIcon from '@mui/icons-material/MyLocation';
import PrintIcon from '@mui/icons-material/Print';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { Link as RouterLink, Navigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import Loading from '../../components/Loading';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { formatCalendarDate, localISODate, visitStatusLabel } from './partnerLabels';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

function hasCoords(point) {
    const lat = Number(point?.latitude);
    const lng = Number(point?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
}

function toLatLng(point) {
    return { lat: Number(point.latitude), lng: Number(point.longitude) };
}

function haversineKm(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Rota gulosa: sempre o próximo ponto mais próximo do atual (ordem das paradas). */
function buildNearestNeighborRoute(origin, stops) {
    const remaining = stops.filter(hasCoords).map((stop) => ({
        ...stop,
        ...toLatLng(stop),
    }));
    if (!hasCoords(origin) || remaining.length === 0) {
        return [];
    }

    const route = [];
    let current = toLatLng(origin);
    while (remaining.length > 0) {
        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < remaining.length; i += 1) {
            const dist = haversineKm(current, remaining[i]);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
        const [next] = remaining.splice(bestIdx, 1);
        route.push({
            ...next,
            order: route.length + 1,
            distance_from_prev_km: bestDist,
        });
        current = { lat: next.lat, lng: next.lng };
    }
    return route;
}

/** Trajeto de carro/moto pelas vias (OSRM). */
async function fetchDrivingPath(waypoints) {
    if (!waypoints || waypoints.length < 2) {
        return null;
    }
    const coords = waypoints
        .map((point) => `${Number(point.lng).toFixed(6)},${Number(point.lat).toFixed(6)}`)
        .join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}`
        + '?overview=full&geometries=geojson&steps=false&annotations=false';
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`OSRM HTTP ${response.status}`);
    }
    const data = await response.json();
    if (data?.code !== 'Ok' || !data?.routes?.[0]) {
        throw new Error(data?.message || 'Rota indisponível');
    }
    const osrmRoute = data.routes[0];
    const positions = (osrmRoute.geometry?.coordinates || []).map(([lng, lat]) => [lat, lng]);
    const legs = (osrmRoute.legs || []).map((leg) => ({
        distance_km: (Number(leg.distance) || 0) / 1000,
        duration_min: (Number(leg.duration) || 0) / 60,
    }));
    return {
        positions,
        legs,
        total_distance_km: (Number(osrmRoute.distance) || 0) / 1000,
        total_duration_min: (Number(osrmRoute.duration) || 0) / 60,
    };
}

function formatKm(km) {
    if (!Number.isFinite(km)) return '—';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1).replace('.', ',')} km`;
}

function formatDuration(minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) return '';
    if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

function stopAddress(stop) {
    return [stop.address, stop.city].filter(Boolean).join(' · ') || 'Sem endereço';
}

/** Separa visualmente marcadores no mesmo GPS (senão um cobre o outro). */
function withDisplayOffsets(stops) {
    const groups = new Map();
    stops.forEach((stop, index) => {
        const key = `${Number(stop.lat).toFixed(5)},${Number(stop.lng).toFixed(5)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(index);
    });

    return stops.map((stop, index) => {
        const key = `${Number(stop.lat).toFixed(5)},${Number(stop.lng).toFixed(5)}`;
        const group = groups.get(key) || [index];
        if (group.length === 1) {
            return {
                ...stop,
                display_lat: stop.lat,
                display_lng: stop.lng,
                shares_location: false,
            };
        }
        const idxInGroup = group.indexOf(index);
        const angle = ((2 * Math.PI) * idxInGroup) / group.length - (Math.PI / 2);
        const radius = 0.00035; // ~35–40 m
        return {
            ...stop,
            display_lat: stop.lat + radius * Math.cos(angle),
            display_lng: stop.lng + radius * Math.sin(angle),
            shares_location: true,
            shared_orders: group.map((i) => stops[i].order),
        };
    });
}

function makeNumberedIcon(label, options = {}) {
    const {
        bg = '#1976d2',
        color = '#fff',
        size = 30,
    } = options;
    return L.divIcon({
        className: 'partners-route-marker',
        html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:${bg};color:${color};border:2px solid #fff;
            box-shadow:0 1px 4px rgba(0,0,0,.35);
            display:flex;align-items:center;justify-content:center;
            font:700 12px/1 'IBM Plex Sans',Arial,sans-serif;
        ">${label}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
    });
}

const originIcon = makeNumberedIcon('P', { bg: '#2e7d32', size: 32 });

function FitRouteBounds({ positions }) {
    const map = useMap();
    useEffect(() => {
        if (!positions?.length) return undefined;
        if (positions.length === 1) {
            map.setView(positions[0], 13);
            return undefined;
        }
        map.fitBounds(positions, { padding: [48, 48] });
        return undefined;
    }, [map, positions]);
    return null;
}

function PrintMapRefresh() {
    const map = useMap();
    useEffect(() => {
        const refresh = () => {
            setTimeout(() => {
                map.invalidateSize();
                map.eachLayer((layer) => {
                    if (layer?.redraw) layer.redraw();
                });
            }, 50);
        };
        window.addEventListener('beforeprint', refresh);
        window.addEventListener('afterprint', refresh);
        return () => {
            window.removeEventListener('beforeprint', refresh);
            window.removeEventListener('afterprint', refresh);
        };
    }, [map]);
    return null;
}

async function searchAddress(query) {
    const q = String(query || '').trim();
    if (q.length < 3) return [];
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('format', 'json');
    url.searchParams.set('q', q);
    url.searchParams.set('limit', '6');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('countrycodes', 'br');
    const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
        throw new Error('Falha ao buscar endereço');
    }
    const data = await response.json();
    return (data || []).map((item) => ({
        label: item.display_name,
        latitude: Number(item.lat),
        longitude: Number(item.lon),
        address: item.display_name,
    }));
}

function PartnersMapPage() {
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [params, setParams] = useSearchParams();
    const [date, setDate] = useState(params.get('date') || localISODate());
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [customOrigin, setCustomOrigin] = useState(null);
    const [originDialogOpen, setOriginDialogOpen] = useState(false);
    const [originQuery, setOriginQuery] = useState('');
    const [originResults, setOriginResults] = useState([]);
    const [originSearching, setOriginSearching] = useState(false);
    const [originError, setOriginError] = useState('');
    const [geoLoading, setGeoLoading] = useState(false);
    const [roadPath, setRoadPath] = useState([]);
    const [roadMeta, setRoadMeta] = useState(null);
    const [routingLoading, setRoutingLoading] = useState(false);
    const [routingError, setRoutingError] = useState('');

    useEffect(() => {
        if (!enabled) return undefined;
        setLoading(true);
        setCustomOrigin(null);
        api.get('/commerce-partners/map', { params: { date } })
            .then((response) => setPayload(response?.data?.data || null))
            .catch(() => setPayload(null))
            .finally(() => setLoading(false));
        return undefined;
    }, [enabled, date]);

    const accountOrigin = useMemo(() => {
        const origin = payload?.origin;
        if (!origin) return null;
        return {
            label: origin.label || 'Sua base',
            address: origin.address || '',
            latitude: origin.latitude,
            longitude: origin.longitude,
            is_account: true,
        };
    }, [payload]);

    const activeOrigin = customOrigin || accountOrigin;

    const pins = useMemo(() => payload?.pins || [], [payload]);

    const stopOrder = useMemo(() => {
        if (!hasCoords(activeOrigin)) return [];
        return buildNearestNeighborRoute(activeOrigin, pins);
    }, [activeOrigin, pins]);

    useEffect(() => {
        let cancelled = false;
        if (!hasCoords(activeOrigin) || stopOrder.length === 0) {
            setRoadPath([]);
            setRoadMeta(null);
            setRoutingError('');
            setRoutingLoading(false);
            return undefined;
        }

        const waypoints = [
            toLatLng(activeOrigin),
            ...stopOrder.map((stop) => ({ lat: stop.lat, lng: stop.lng })),
        ];

        setRoutingLoading(true);
        setRoutingError('');
        fetchDrivingPath(waypoints)
            .then((result) => {
                if (cancelled || !result) return;
                setRoadPath(result.positions);
                setRoadMeta(result);
            })
            .catch(() => {
                if (cancelled) return;
                setRoadPath([]);
                setRoadMeta(null);
                setRoutingError('Não foi possível traçar a rota pelas vias. Exibindo ordem das paradas sem trajeto.');
            })
            .finally(() => {
                if (!cancelled) setRoutingLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [activeOrigin, stopOrder]);

    const route = useMemo(() => {
        if (!roadMeta?.legs?.length) return stopOrder;
        return stopOrder.map((stop, index) => {
            const leg = roadMeta.legs[index];
            if (!leg) return stop;
            return {
                ...stop,
                distance_from_prev_km: leg.distance_km,
                duration_from_prev_min: leg.duration_min,
            };
        });
    }, [stopOrder, roadMeta]);

    const mapStops = useMemo(() => withDisplayOffsets(route), [route]);

    const overlappingStops = useMemo(
        () => mapStops.filter((stop) => stop.shares_location),
        [mapStops],
    );

    const withoutCoords = useMemo(
        () => pins.filter((pin) => !hasCoords(pin)),
        [pins],
    );

    const totalDistanceKm = roadMeta?.total_distance_km
        ?? route.reduce((sum, stop) => sum + (Number(stop.distance_from_prev_km) || 0), 0);
    const totalDurationMin = roadMeta?.total_duration_min;

    const mapPositions = useMemo(() => {
        if (roadPath.length > 1) return roadPath;
        const points = [];
        if (hasCoords(activeOrigin)) {
            points.push([Number(activeOrigin.latitude), Number(activeOrigin.longitude)]);
        }
        route.forEach((stop) => {
            points.push([stop.lat, stop.lng]);
        });
        return points;
    }, [activeOrigin, route, roadPath]);

    const mapCenter = mapPositions[0] || [-23.55, -46.63];
    const drawsRoadPath = roadPath.length > 1;

    const openOriginDialog = () => {
        setOriginQuery(customOrigin?.address || accountOrigin?.address || '');
        setOriginResults([]);
        setOriginError('');
        setOriginDialogOpen(true);
    };

    const handleSearchOrigin = async () => {
        setOriginSearching(true);
        setOriginError('');
        try {
            const results = await searchAddress(originQuery);
            setOriginResults(results);
            if (results.length === 0) {
                setOriginError('Nenhum endereço encontrado. Tente ser mais específico.');
            }
        } catch {
            setOriginError('Não foi possível buscar o endereço agora.');
            setOriginResults([]);
        } finally {
            setOriginSearching(false);
        }
    };

    const applyOrigin = (point) => {
        setCustomOrigin({
            label: 'Partida personalizada',
            address: point.address || point.label || 'Partida personalizada',
            latitude: point.latitude,
            longitude: point.longitude,
            is_account: false,
        });
        setOriginDialogOpen(false);
    };

    const useCurrentLocation = () => {
        if (!navigator.geolocation) {
            setOriginError('Geolocalização não disponível neste dispositivo.');
            return;
        }
        setGeoLoading(true);
        setOriginError('');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCustomOrigin({
                    label: 'Minha localização',
                    address: `Lat ${position.coords.latitude.toFixed(5)}, Lng ${position.coords.longitude.toFixed(5)}`,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    is_account: false,
                });
                setGeoLoading(false);
                setOriginDialogOpen(false);
            },
            () => {
                setOriginError('Não foi possível obter sua localização.');
                setGeoLoading(false);
            },
            { enableHighAccuracy: true, timeout: 12000 },
        );
    };

    const resetOrigin = () => {
        setCustomOrigin(null);
    };

    if (flagLoading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;

    return (
        <Box className="partners-map-page">
            <Breadcrumbs sx={{ mb: 2 }} className="no-print">
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Typography color="text.primary">Mapa do dia</Typography>
            </Breadcrumbs>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 2 }}
                className="no-print"
                alignItems={{ sm: 'center' }}
            >
                <Typography variant="h5" sx={{ flex: 1 }}>Mapa / rota do dia</Typography>
                <TextField
                    type="date"
                    label="Data"
                    InputLabelProps={{ shrink: true }}
                    value={date}
                    onChange={(e) => {
                        setDate(e.target.value);
                        setParams({ date: e.target.value });
                    }}
                />
                <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => {
                        window.dispatchEvent(new Event('resize'));
                        setTimeout(() => window.print(), 250);
                    }}
                >
                    Imprimir rota
                </Button>
            </Stack>

            {loading ? <Loading /> : (
                <>
                    <Box className="print-map-sheet">
                        <Paper sx={{ p: 2, mb: 2 }} className="print-origin-block">
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.5}
                                alignItems={{ md: 'center' }}
                                justifyContent="space-between"
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="overline" color="text.secondary">Ponto de partida</Typography>
                                    <Typography fontWeight={700} className="print-origin-title">
                                        {activeOrigin?.label || 'Sem origem'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" className="print-origin-address">
                                        {activeOrigin?.address || 'Defina um endereço para montar a rota.'}
                                    </Typography>
                                    {!hasCoords(activeOrigin) ? (
                                        <Alert severity="warning" sx={{ mt: 1 }} className="no-print">
                                            Sem coordenadas na partida — altere o ponto de partida ou cadastre o endereço em Minha conta.
                                        </Alert>
                                    ) : null}
                                    {customOrigin ? (
                                        <Chip
                                            size="small"
                                            color="info"
                                            label="Partida personalizada"
                                            sx={{ mt: 1 }}
                                            className="no-print"
                                        />
                                    ) : null}
                                </Box>
                                <Stack direction="row" spacing={1} className="no-print" flexWrap="wrap" useFlexGap>
                                    <Button variant="outlined" onClick={openOriginDialog}>
                                        Alterar partida
                                    </Button>
                                    {customOrigin ? (
                                        <Button
                                            variant="text"
                                            startIcon={<RestartAltIcon />}
                                            onClick={resetOrigin}
                                        >
                                            Usar meu endereço
                                        </Button>
                                    ) : null}
                                </Stack>
                            </Stack>
                        </Paper>

                        <Paper
                            sx={{ height: { xs: 360, md: 480 }, mb: 2, overflow: 'hidden', position: 'relative' }}
                            className="print-map"
                        >
                            {routingLoading ? (
                                <Box
                                    className="no-print"
                                    sx={{
                                        position: 'absolute',
                                        zIndex: 1000,
                                        top: 12,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        bgcolor: 'background.paper',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1,
                                        boxShadow: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <CircularProgress size={16} />
                                    <Typography variant="caption">Calculando rota pelas vias…</Typography>
                                </Box>
                            ) : null}
                            <MapContainer
                                key={`${date}-${activeOrigin?.latitude}-${activeOrigin?.longitude}-${route.length}-${drawsRoadPath}`}
                                center={mapCenter}
                                zoom={12}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <FitRouteBounds positions={mapPositions} />
                                <PrintMapRefresh />
                                {drawsRoadPath ? (
                                    <Polyline
                                        positions={roadPath}
                                        pathOptions={{ color: '#1976d2', weight: 5, opacity: 0.9 }}
                                    />
                                ) : null}
                                {hasCoords(activeOrigin) ? (
                                    <Marker
                                        position={[Number(activeOrigin.latitude), Number(activeOrigin.longitude)]}
                                        icon={originIcon}
                                    >
                                        <Popup>
                                            <strong>{activeOrigin.label}</strong><br />
                                            {activeOrigin.address}
                                        </Popup>
                                    </Marker>
                                ) : null}
                                {mapStops.map((stop) => (
                                    <Marker
                                        key={stop.visit_uuid || `${stop.partner_uuid}-${stop.order}`}
                                        position={[stop.display_lat, stop.display_lng]}
                                        icon={makeNumberedIcon(String(stop.order))}
                                        zIndexOffset={1000 - stop.order}
                                    >
                                        <Popup>
                                            <strong>{stop.order}. {stop.label}</strong><br />
                                            {stopAddress(stop)}<br />
                                            {formatKm(stop.distance_from_prev_km)}
                                            {stop.duration_from_prev_min
                                                ? ` · ${formatDuration(stop.duration_from_prev_min)}`
                                                : ''} desde o ponto anterior
                                            {stop.shares_location ? (
                                                <>
                                                    <br />
                                                    <em>Mesmo GPS das paradas {stop.shared_orders.join(', ')} — marcador deslocado no mapa.</em>
                                                </>
                                            ) : null}
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </Paper>
                    </Box>

                    {routingError ? (
                        <Alert severity="warning" sx={{ mb: 2 }} className="no-print">{routingError}</Alert>
                    ) : null}

                    {overlappingStops.length > 0 ? (
                        <Alert severity="info" sx={{ mb: 2 }} className="no-print">
                            Algumas paradas ainda compartilham o mesmo GPS. Ao abrir o mapa, o sistema tenta recalcular
                            as coordenadas pelo número do endereço — recarregue a página se acabou de corrigir um cadastro.
                        </Alert>
                    ) : null}

                    <Box className="print-itinerary-sheet">
                    <Paper sx={{ p: 2 }} className="print-itinerary">
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ sm: 'center' }}
                            sx={{ mb: 1.5 }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6">
                                    Itinerário · {formatCalendarDate(date)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {route.length} parada{route.length === 1 ? '' : 's'}
                                    {route.length > 0 ? ` · ~${formatKm(totalDistanceKm)}` : ''}
                                    {totalDurationMin ? ` · ~${formatDuration(totalDurationMin)}` : ''}
                                    {drawsRoadPath ? ' · trajeto de carro/moto pelas vias' : ''}
                                </Typography>
                            </Box>
                        </Stack>

                        {route.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {pins.length === 0
                                    ? 'Nenhuma visita neste dia.'
                                    : 'Não foi possível montar a rota. Defina a partida e confira se os parceiros têm coordenadas.'}
                            </Typography>
                        ) : (
                            <Stack spacing={0}>
                                <Box
                                    sx={{
                                        py: 1.25,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '40px 1fr', sm: '48px 1fr auto' },
                                        gap: 1,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Chip size="small" label="P" color="success" sx={{ width: 36, fontWeight: 700 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight={700}>{activeOrigin?.label}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {activeOrigin?.address}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: { xs: 'none', sm: 'block' } }}
                                    >
                                        Partida
                                    </Typography>
                                </Box>

                                {route.map((stop) => (
                                    <Box
                                        key={stop.visit_uuid || stop.partner_uuid}
                                        sx={{
                                            py: 1.25,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '40px 1fr', sm: '48px 1fr auto' },
                                            gap: 1,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            label={stop.order}
                                            color="primary"
                                            sx={{ width: 36, fontWeight: 700 }}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography fontWeight={700}>{stop.label}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {stopAddress(stop)}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                                                <Chip size="small" label={visitStatusLabel(stop.status)} />
                                                <Typography variant="caption" color="text.secondary">
                                                    +{formatKm(stop.distance_from_prev_km)}
                                                    {stop.duration_from_prev_min
                                                        ? ` · ${formatDuration(stop.duration_from_prev_min)}`
                                                        : ''} do ponto anterior
                                                </Typography>
                                            </Stack>
                                        </Box>
                                        <Stack direction="row" spacing={1} className="no-print" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                            {stop.partner_uuid ? (
                                                <Button
                                                    size="small"
                                                    component={RouterLink}
                                                    to={`/admin/partners/${stop.partner_uuid}`}
                                                >
                                                    Detalhes
                                                </Button>
                                            ) : null}
                                            {stop.visit_uuid && stop.status !== 'done' ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    component={RouterLink}
                                                    to={`/admin/partners/visits/${stop.visit_uuid}`}
                                                >
                                                    + Visita
                                                </Button>
                                            ) : null}
                                            {stop.visit_uuid && stop.status === 'done' ? (
                                                <Button
                                                    size="small"
                                                    component={RouterLink}
                                                    to={`/admin/partners/visits/${stop.visit_uuid}/receipt`}
                                                >
                                                    Comprovante
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {withoutCoords.length > 0 ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="warning.main">
                                    Sem coordenadas (fora da rota automática)
                                </Typography>
                                {withoutCoords.map((pin) => (
                                    <Typography key={pin.visit_uuid || pin.partner_uuid} variant="body2" sx={{ mt: 0.5 }}>
                                        {pin.label} — {stopAddress(pin)}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}
                    </Paper>
                    </Box>

                    <Dialog
                        open={originDialogOpen}
                        onClose={() => setOriginDialogOpen(false)}
                        fullWidth
                        maxWidth="sm"
                        className="no-print"
                    >
                        <DialogTitle>Alterar ponto de partida</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Use quando for começar o percurso a partir de outro lugar (ex.: um compromisso).
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                                <TextField
                                    label="Buscar endereço"
                                    value={originQuery}
                                    onChange={(e) => setOriginQuery(e.target.value)}
                                    fullWidth
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchOrigin();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSearchOrigin}
                                    disabled={originSearching || originQuery.trim().length < 3}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {originSearching ? <CircularProgress size={20} color="inherit" /> : 'Buscar'}
                                </Button>
                            </Stack>
                            <Button
                                size="small"
                                startIcon={geoLoading ? <CircularProgress size={14} /> : <MyLocationIcon />}
                                onClick={useCurrentLocation}
                                disabled={geoLoading}
                                sx={{ mb: 1 }}
                            >
                                Usar minha localização atual
                            </Button>
                            {originError ? <Alert severity="error" sx={{ mb: 1 }}>{originError}</Alert> : null}
                            <List dense>
                                {originResults.map((result) => (
                                    <ListItemButton
                                        key={`${result.latitude}-${result.longitude}-${result.label}`}
                                        onClick={() => applyOrigin(result)}
                                    >
                                        <ListItemText
                                            primary={result.label}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </DialogContent>
                        <DialogActions>
                            {accountOrigin && hasCoords(accountOrigin) ? (
                                <Button
                                    onClick={() => {
                                        resetOrigin();
                                        setOriginDialogOpen(false);
                                    }}
                                    sx={{ mr: 'auto' }}
                                >
                                    Restaurar meu endereço
                                </Button>
                            ) : null}
                            <Button onClick={() => setOriginDialogOpen(false)}>Fechar</Button>
                        </DialogActions>
                    </Dialog>

                    <style>{`
                        .partners-route-marker { background: transparent; border: 0; }
                        @media print {
                            @page {
                                size: A4 landscape;
                                margin: 8mm;
                            }

                            html, body {
                                background: #fff !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }

                            body * { visibility: hidden !important; }
                            .partners-map-page,
                            .partners-map-page * { visibility: visible !important; }

                            .MuiAppBar-root,
                            .MuiDrawer-root,
                            .MuiDrawer-root *,
                            header,
                            nav,
                            .no-print {
                                display: none !important;
                                visibility: hidden !important;
                            }

                            .partners-map-page {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                background: #fff !important;
                                color: #111 !important;
                            }

                            .partners-map-page .print-map-sheet {
                                break-after: page;
                                page-break-after: always;
                                display: flex;
                                flex-direction: column;
                                height: calc(100vh - 16mm);
                                min-height: 170mm;
                            }

                            .partners-map-page .print-origin-block {
                                box-shadow: none !important;
                                border: 0 !important;
                                background: transparent !important;
                                margin: 0 0 6mm !important;
                                padding: 0 !important;
                            }

                            .partners-map-page .print-origin-title {
                                white-space: normal !important;
                                color: #111 !important;
                            }

                            .partners-map-page .print-origin-address {
                                color: #444 !important;
                                white-space: normal !important;
                            }

                            .partners-map-page .print-map {
                                flex: 1 1 auto !important;
                                height: auto !important;
                                min-height: 140mm !important;
                                margin: 0 !important;
                                box-shadow: none !important;
                                border: 1px solid #ccc !important;
                                border-radius: 0 !important;
                                overflow: hidden !important;
                                break-inside: avoid;
                            }

                            .partners-map-page .print-map .leaflet-container {
                                height: 100% !important;
                                width: 100% !important;
                            }

                            .partners-map-page .print-itinerary-sheet {
                                break-before: page;
                                page-break-before: always;
                            }

                            .partners-map-page .print-itinerary {
                                box-shadow: none !important;
                                border: 0 !important;
                                background: transparent !important;
                                padding: 0 !important;
                                color: #111 !important;
                            }

                            .partners-map-page .print-itinerary .MuiTypography-root,
                            .partners-map-page .print-itinerary .MuiChip-label {
                                color: #111 !important;
                            }

                            .partners-map-page .print-itinerary .MuiChip-root {
                                border: 1px solid #999 !important;
                                background: #f3f3f3 !important;
                            }
                        }
                    `}</style>
                </>
            )}
        </Box>
    );
}

export default PartnersMapPage;
